import React, { FC, memo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useStyles } from './Login.styles';
import { useAuth, useSignIn } from '@shared/hooks/useAuth';
import { paths } from '@shared/enums/paths';
import { useTitle } from '@shared/hooks/useTitle';
import { pageTitles } from '@shared/enums/pageTitles';
import { RockyButton } from '@shared/components/RockyButton';
import { Alert, AlertTitle, Stack, Typography } from '@mui/material';
import { ReactComponent as GoogleIcon } from '@assets/icons/googleIcon.svg';
import { authUserErrorSelector, authUserState } from '@modules/App/redux/user/selectors';
import { useSelector } from 'react-redux';
import { ReactComponent as RockyLogoSign } from '@assets/icons/RockyLogoSign.svg';
import { DataState } from '@shared/enums/DataState';
import { RocketLoader } from '@shared/components/RocketLoader';
import { checkIsSessionExpired } from '@shared/helpers/auth';

const LoginComponent: FC = () => {
  const styles = useStyles();
  const { currentUser } = useAuth();
  const error = useSelector(authUserErrorSelector);
  const currentUserDataState = useSelector(authUserState);

  const navigate = useNavigate();
  const setPageTitle = useTitle();

  useEffect(() => {
    setPageTitle(pageTitles.loginPage);
  }, []);

  useEffect(() => {
    if (currentUser) {
      navigate(paths.myProfile);
    }
  }, [currentUser]);

  const signIn = useSignIn();

  const isSessionExpired = checkIsSessionExpired(error?.message ?? '');
  const isLoading = currentUserDataState === DataState.Pending;
  return (
    <div className={styles.root}>
      {isLoading ? (
        <div className={styles.loaderHolder}>
          <RocketLoader />
        </div>
      ) : (
        <>
          <RockyLogoSign />
          <Stack gap="8px" alignItems="center" mt="40px">
            <Typography variant="h2" textAlign="center">
              Sign in with your AgileEngine email
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Rocky is accessible only for AgileEngineers
            </Typography>
            <RockyButton className={styles.loginButton} onClick={signIn} action>
              <GoogleIcon className={styles.googleLogo} />
              Sign in with Google
            </RockyButton>
          </Stack>
          {isSessionExpired && (
            <Alert
              icon={false}
              severity="warning"
              sx={{
                width: '348px',
                mt: '40px',
              }}
            >
              <AlertTitle>Your session expired</AlertTitle>
              <Typography variant="body2" color="text.primary">
                For the security reasons, the system now signs you out automatically when you haven’t been active for a
                day. All your data was saved.
              </Typography>
            </Alert>
          )}
        </>
      )}
    </div>
  );
};

export const Login = memo(LoginComponent);
